import { Client } from "./client.interface";
import { Image } from "./general.interface";
import { Job } from "./job.interface";
import {
    FullScreenJobHeaderPageComponent,
    SidecarJobBodyPageComponent,
    SimpleTwoColBodyPageComponent,
    TypographicalHeaderPageComponent,
    TypographicalImageHeaderPageComponent,
    TypographicalSearchHeaderPageComponent,
} from "./new-templates.interface";
import { Page } from "./page.interface";
import { ButtonConfig, Template, TemplateRow } from "./template.interface";

export enum PageComponentTypes {
    TEXT = "text",
    CTA = "cta",
    HEADER = "header",
    IMAGE_TEXT = "imageWithText",
    IMAGE = "images",
    MAP = "map",
    QUOTE = "quote",
    VIDEO = "video",
    JOBS = "jobs",
    JOB_BOARD = "jobBoard",
    GRID = "grid",
    JOB_BODY = "jobBody",
    JOB_SIDEBAR = "jobSidebar",
    JOB_HEADER = "jobHeader",
    FOOTER = "footer",
    ACCORDION = "accordion",
    CAROUSEL = "carousel",
    BREADCRUMBS = "breadcrumbs",
    JD_MAIN_HEADER = "jdMainHeader",
    JD_JOB_BODY = "jdJobBody",
    JD_LINK_GROUP = "jdLinkGroup",
    JD_INNER_PAGE_HEADER = "jdInnerPageHeader",
    JD_SECTION_HEADER = "jdSectionHeader",
    JD_JOB_SEARCH_HEADER = "jdJobSearchHeader",
    JD_JOB_BOARD = "jdJobBoard",
    JD_LINKEDIN_FEED = "jdLinkedInFeed",
    JD_TIMELINE = "jdTimeline",
    JD_ANIMATED_STATS = "jdAnimatedStats",
    JD_OUR_VALUES = "jdOurValues",
    JD_OUR_COMMITMENTS = "jdOurCommitments",
    JD_FRANCE_DEPARTMENTS_TABS = "jdFranceDepartmentsTabs",
    JD_FRANCE_TRAINING_COURSES_TABS = "jdFranceTrainingCoursesTabs", 
    JD_FRANCE_STORE_JOBS = "jdFranceStoreJobs",
    JD_FRANCE_SUCCESS_STORY = "jdFranceSuccessStory",
    SOHO_SEARCH_BOX = "sohoSearchBox",
    TYPOGRAPHICAL_JOB_HEADER = "typographicalJobHeader",
    TYPOGRAPHICAL_IMAGE_JOB_HEADER = "typographicalImageJobHeader",
    TYPOGRAPHICAL_SEARCH_HEADER = "typographicalSearchHeader",
    SIDECAR_JOB_BODY = "sidebarJobBody",
    FULLSCREEN_JOB_HEADER = "fullscreenJobHeader",
    SIMPLE_TWO_COL_BODY = "simpleTwoColBody",
    TYPOGRAPHICAL_JOB_BOARD = "typographicalJobBoard",
    MODERN_JOB_BOARD = "modernJobBoard",
    NEON_JOB_BOARD = "neonJobBoard",
    NEON_JOB_BODY = "neonJobBody",
    ALL_IN_ONE_SEARCH_HEADER = "allInOneSearchHeader",
    JOB_CARDS = "jobCards",
    JD_FRANCE_HEADER = "jdFranceHeader",
    JD_FRANCE_INCLUSION_TABS = "jdFranceInclusionTabs",
    JD_PULLQUOTE = "jdPullquote",
    MEET_THE_TEAM = "meetTheTeam",
}
export enum GridComponentTypes {
    TEXT = "text",
    IMAGE = "image",
    QUOTE = "quote",
    VIDEO = "video",
    JOB = "job",
    MAP = "map",
    JOB_HEADER = "jobHeader",
    JOB_DETAILS = "jobDetails",
}

export enum ComponentActionTypes {
    BLANK,
    EDIT_COMPONENT,
    IFRAME_LOADED,
    ROW_HIGHLIGHTED,
    ROW_SELECTED,
    COMPONENT_HIGHLIGHTED,
    COMPONENT_SELECTED,
    GRID_COMPONENT_HIGHLIGHTED,
    GRID_COMPONENT_SELECTED,
    TEMPLATE_LOADED,
    TEMPLATE_UPDATED,
    COMPONENT_UPDATED,
    GRID_COMPONENT_UPDATED,
    ROW_UPDATED,
    ITEM_ADDED,
    ITEM_HIGHLIGHTED,
    ITEM_SELECTED,
    ITEM_UPDATED,
    ITEM_DELETED,
    HEADER_SELECTED,
    FOOTER_SELECTED,
    THEME_SETTINGS_SELECTED,
    DESKTOP_VIEW_SELECTED,
    TABLET_VIEW_SELECTED,
    MOBILE_VIEW_SELECTED,
    ROW_CLICKED,
    ROW_DELETED,
    ROW_CLONED,
    COMPONENT_DELETED,
    GRID_COMPONENT_DELETED,
    THEME_UPDATED,
    EDITOR_SAVED,
    NAVIGATE_SIDEBAR,
    TOGGLE_LOCKED,
    TOGGLE_DARK_MODE,
}
export interface TriggeredComponentAction {
    componentIndex: number;
    action: ComponentActionTypes;
    rowIndex?: number;
    gridIndex?: number;
    itemIndex?: number;
    content?: string;
    templateData?: Template;
    componentData?: VoyseComponent;
    gridComponentData?: VoyseGridComponent;
    itemData?: TextItem;
    themeData?: Client;
    rowData?: TemplateRow;
}

export interface Colour {
    type: string;
    value: string;
}

export interface HasOverlay {
    overlayOpacity: string;
}

export interface HasImage {
    image: Image;
    mobileImage: Image;
    scaling: string;
    altText: string;
}

export interface HasVideo {
    source: string;
    video: string;
    videoUrl?: string;
    playlist?: boolean;
    autoplay?: boolean;
}

export interface HasUrl {
    url: string;
    caption: string;
}

export interface HasText {
    typeface: string;
    textSize: string;
    alignText: string;
    text: string;
}

export interface TextItem {
    text: string;
    textType: string;
    alignText: string;
    typeface: string;
    textSize: string;
    source: string;
}

export interface AccordionItem {
    title: string;
    summary: string;
    content: string;
}

export interface VoyseBaseComponent {
    colspan: number;
    mobile_colspan: number;
    showMobile: boolean;
    type: string;
    componentIndex: number;
    rowIndex: number;
    editableWhenLocked: boolean;
    page?: Page | Job;
}

export interface VoyseBaseGridComponent extends VoyseBaseComponent {
    rowspan: number;
    mobile_rowspan: number;
}

export interface VoyseBasePageComponent extends VoyseBaseComponent {}
export interface VideoPageComponent extends VoyseBasePageComponent, HasVideo {
    source: string;
    video: string;
    autoplay: boolean;
    playlist: boolean;
    caption?: string;
    aspectRatio?: string;
    mobileAspectRatio?: string;
    videoUrl?: string;
}

export interface ImagePageComponent extends HasImage, VoyseBasePageComponent {}

export interface QuotePageComponent extends HasImage, HasOverlay, VoyseBasePageComponent {
    typeface: string;
    textSize: string;
    alignText: string;
    text: string;
    name: string;
    minHeight?: string;
}

export interface FooterPageComponent extends VoyseBasePageComponent {
    background: Colour;
    textColour: string;
}

export interface JobsPageComponent extends HasImage, VoyseBasePageComponent {
    selectedJob: string;
    background: Colour;
    textColour: string;
    buttonConfig: ButtonConfig;
    darkMode: boolean;
}

export interface JobBoardPageComponent extends VoyseBasePageComponent {
    showSearch: boolean;
    hideJobs: boolean;
    redirectToPage: string;
    resultsPerPage: number;
    buttonConfig: ButtonConfig;
    moreDetailsButtonConfig: ButtonConfig;
    internalJobs: boolean;
    cards: {
        hideCategory: boolean;
        hideBusinessUnit: boolean;
        hideJobType: boolean;
        hideMedia: boolean;
        hideSummary: boolean;
        preferSummaryOrDescription?: "summary" | "description";
        hideLogo: boolean;
        categoryHighlightColour: string;
        darkMode: boolean;
    };
    filterJobs: {
        category: string;
        location: string;
        hashtags: string;
        seniority: string;
        businessUnit: string;
        lang: string;
    };
    showFilters: {
        category: boolean;
        locationLabel: boolean;
        keywords: boolean;
        hashtags: boolean;
        seniority: boolean;
        businessUnit: boolean;
        reference: boolean;
        geo: boolean;
    };
    filterLabels?: {
        keywords: string;
        category: string;
        businessUnit: string;
    };
    hideGreenhouseLocationLevels: number[];
}

export interface ImageWithTextPageComponent extends HasUrl, HasImage, TextItem, VoyseBasePageComponent {
    background: Colour;
    textColour: string;
}

export interface ImagePageComponent extends HasUrl, HasImage, VoyseBasePageComponent {}

export interface HeaderPageComponent extends HasImage, HasText, HasUrl, HasOverlay, VoyseBasePageComponent {
    source: string;
    video: string;
    autoplay?: boolean;
    strapline: string;
    background: Colour;
    textColour: string;
    captureCV: boolean;
    cta?: boolean;
    ctaType?: string;
    jobTextOverride?: string;
    target?: string;
    buttonText?: string;
    buttonConfig: ButtonConfig;
    showMoreDetailsButton?: boolean;
    showApplyButton?: boolean
    // LEGACY
    ctaText?: string;
    minHeight?: string;
    hideActions?: boolean;
}

export interface CtaPageComponent extends HasUrl, VoyseBasePageComponent {
    captureCV: boolean;
    buttonText: string;
    ctaType: string;
    target?: string;
    dialogTitle?: string;
    buttonConfig: ButtonConfig;
}
export interface BasicTextPageComponent extends VoyseBasePageComponent {
    background: Colour;
    textColour: string;
    text: string;
    typeface: string[];
    padding: number;
    mobilePadding: number;
    alignTextVertical: string;
}

export interface GridPageComponent extends VoyseBasePageComponent {
    components: VoyseGridComponent[];
    columns: number;
    mobileColumns: number;
    gutterSize: string;
}

export interface JobBodyPageComponent extends VoyseBasePageComponent, HasVideo {
    showSummary: boolean;
    textColour: string;
    videoPosition: "before" | "after" | "beforeSummary";
}

export interface JobSidebarPageComponent extends VoyseBaseComponent {
    showTitle: boolean;
    showCategory: boolean;
    showPostedDate: boolean;
    showReferButton: boolean;
    showLocation: boolean;
    showJobReference: boolean;
    showJobType: boolean;
    showSalary: boolean;
    applyButton: ButtonConfig;
    referButton: ButtonConfig;
    textColour: string;
    logo: Image;
}

export interface BreadCrumbsPageComponent extends VoyseBaseComponent {
    showCategory: boolean;
    showBusinessUnit: boolean;
    darkMode: boolean;
    jobsPage: string;
}

export interface AccordionPageComponent extends VoyseBaseComponent {
    items: AccordionItem[];
    darkMode: boolean;
}

export enum CarouselCardTypes {
    JOB = "job",
    IMAGE = "image",
    VIDEO = "video",
    LINK = "link",
}

export interface CarouselPageComponent extends VoyseBaseComponent {
    cardType: CarouselCardTypes;
    items?: CarouselCard[];
    showArrows?: boolean;
    showFooter?: boolean;
    showCategory?: boolean;
    showLocation?: boolean;
    showGroups?: boolean;
    categoryHighlightColor?: string;
    jobFilters?: {
        [key: string]: string | string[] | boolean | number;
    };
    buttonConfig?: {
        text: string;
        bgColor: string;
        textColor: string;
        borderColor: string;
        borderRadius: string;
    };
}

export interface CarouselCard {
    title?: string;
    job?: Job;
    content?: string;
    image?: Image;
    video?: string;
    link?: string;
    aspectRatio?: string;
    group?: string;
}

// CUSTOM COMPONENTS
export interface JdJobBodyPageComponent extends VoyseBaseComponent {
    job: Job;
    image: Image;
    mobileImage: Image;
    logo: Image;
    scaling: string;
    theme: "jd" | "size" | "gooutdoors";
}

export interface JdInnerPageHeaderPageComponent extends VoyseBaseComponent {
    title?: string;
    video?: string;
    image?: Image;
    mobileImage?: Image;
    scaling?: string;
}

export interface JdMainHeaderPageComponent extends VoyseBaseComponent {
    title?: string;
    video?: string;
    mobileVideo?: string;
    autoplay?: boolean;
    aspectRatio?: string;
}

export interface JdSectionHeaderPageComponent extends VoyseBaseComponent {
    title: string;
    content?: string;
    eyebrow?: string;
}

export interface JdDepartmentsGridComponent extends VoyseBaseComponent {}

export interface JdLinkGroupItem {
    title: string;
    link: string;
}

export interface JdLinkGroupPageComponent extends VoyseBaseComponent {
    columns: number;
    items: JdLinkGroupItem[];
}

export interface JdJobSearchHeaderPageComponent extends VoyseBaseComponent {
    hideImage?: boolean;
    image: Image;
    mobileImage: Image;
    scaling: string;
    title: string;
}

export interface JdFranceHeaderPageComponent extends VoyseBaseComponent, HasImage {
    title: string;
    subtitle: string;
}

export interface JdJobBoardPageComponent extends VoyseBaseComponent {
}

export interface JdLinkedInFeedPageComponent extends VoyseBaseComponent {}

export interface JdTimelinePageComponent extends VoyseBaseComponent {}

export interface JdAnimatedStatsPageComponent extends VoyseBaseComponent {}

export interface JdOurValuesPageComponent extends VoyseBaseComponent {}

export interface JdOurCommitmentsPageComponent extends VoyseBaseComponent {}

export interface JdFranceDepartmentTabsPageComponent extends VoyseBaseComponent {}

export interface JdFranceTrainingCoursesTabsPageComponent extends VoyseBaseComponent {}

export interface JdFranceStoreJobsPageComponent extends VoyseBaseComponent {}

export interface JdFranceSuccessStoryPageComponent extends VoyseBaseComponent {
    selectedVideos: string[];
    source: string;
    aspectRatio: string;
}

export interface SohoSearchBoxComponent extends VoyseBaseComponent {}

export interface VideoGridComponent extends VoyseBaseGridComponent, HasVideo {}

export interface TextGridComponent extends HasImage, HasOverlay, VoyseBaseGridComponent {
    background: Colour;
    textColour: string;
    items: TextItem[];
    scaling: string;
}

export interface QuoteGridComponent extends HasText, HasImage, HasOverlay, VoyseBaseGridComponent {
    name: string;
    title: string;
}

export interface JobGridComponent extends VoyseBaseGridComponent {
    job: string;
    video: string;
    background: Colour;
    textColour: string;
}

export interface JobHeaderGridComponent extends HasImage, HasOverlay, VoyseBaseGridComponent {
    background: Colour;
    textColour: string;
    textSize: string;
    typeface: string;
    fontWeight: string;
    buttonConfig: ButtonConfig;
    hideActions?: boolean;
    hideButtons?: boolean;
}

export interface JobDetailsGridComponent extends VoyseBaseGridComponent {
    background: Colour;
    textColour: string;
}

export interface ImageGridComponent extends HasImage, HasUrl, VoyseBaseGridComponent {
    scaling: string;
}

export interface JdFranceInclusionTabsPageComponent extends VoyseBaseComponent {}

export interface JdPullquotePageComponent extends HasImage, VoyseBaseComponent {
    title: string;
    content: string;
}

export interface TeamMember {
    name: string;
    jobtitle: string;
    linkedin: string;
    description: string;
    image: Image;
}

export interface MeetTheTeamPageComponent extends VoyseBaseComponent {
    title: string;
    content: string;
    layout: string;
    teamMembers: TeamMember[];
}

export type VoysePageComponent =
    | VideoPageComponent
    | QuotePageComponent
    | FooterPageComponent
    | JobBoardPageComponent
    | ImageWithTextPageComponent
    | HeaderPageComponent
    | CtaPageComponent
    | BasicTextPageComponent
    | ImagePageComponent
    | GridPageComponent
    | JobBodyPageComponent
    | JobSidebarPageComponent
    | AccordionPageComponent
    | CarouselPageComponent
    | JdJobBodyPageComponent
    | JdMainHeaderPageComponent
    | JdDepartmentsGridComponent
    | JdSectionHeaderPageComponent
    | JdInnerPageHeaderPageComponent
    | JdLinkGroupPageComponent
    | JdTimelinePageComponent
    | JdAnimatedStatsPageComponent
    | JdOurValuesPageComponent
    | JdOurCommitmentsPageComponent
    | JdFranceDepartmentTabsPageComponent
    | JdFranceTrainingCoursesTabsPageComponent
    | JdFranceStoreJobsPageComponent
    | JdFranceSuccessStoryPageComponent
    | BreadCrumbsPageComponent
    | TypographicalHeaderPageComponent
    | SidecarJobBodyPageComponent
    | FullScreenJobHeaderPageComponent
    | TypographicalImageHeaderPageComponent
    | SimpleTwoColBodyPageComponent
    | FullScreenJobHeaderPageComponent
    | TypographicalSearchHeaderPageComponent
    | JdFranceHeaderPageComponent
    | JdFranceInclusionTabsPageComponent
    | JdPullquotePageComponent
    | MeetTheTeamPageComponent;
export type VoyseGridComponent =
    | VideoGridComponent
    | QuoteGridComponent
    | JobGridComponent
    | JobHeaderGridComponent
    | JobDetailsGridComponent
    | TextGridComponent
    | ImageGridComponent;

export type VoyseComponent = VoysePageComponent | VoyseGridComponent;

export interface VoyseComponentConfig {
    components: VoyseComponent[];
}
